"use client";

import { c as _c } from "react/compiler-runtime";
import { useCallback, useMemo, useRef, useState, type CSSProperties } from "react";
import styles from "./use-drawer-resize.module.css";
const MIN_RATIO = 0;
const MAX_RATIO = 0.92;
const INITIAL_RATIO = 0.75;
const DISMISS_RATIO = 0.3;
const viewportHeight = () => window.visualViewport?.height ?? window.innerHeight;
const heightFromRatio = (ratio: number) => Math.round(viewportHeight() * ratio);
const clampHeight = (px: number) => {
  const vh = viewportHeight();
  return Math.round(Math.min(vh * MAX_RATIO, Math.max(vh * MIN_RATIO, px)));
};
export function useDrawerResize(onDismiss) {
  const $ = _c(20);
  const [height, setHeightState] = useState(0);
  const heightRef = useRef(0);
  const dragRef = useRef(null);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = next => {
      heightRef.current = next;
      setHeightState(next);
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const setHeight = t0;
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      setHeight(heightFromRatio(INITIAL_RATIO));
    };
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const reset = t1;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = e => {
      e.preventDefault();
      e.stopPropagation();
      e.currentTarget.setPointerCapture(e.pointerId);
      dragRef.current = {
        startY: e.clientY,
        startH: heightRef.current
      };
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const onPointerDown = t2;
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = e_0 => {
      if (!dragRef.current || !e_0.currentTarget.hasPointerCapture(e_0.pointerId)) {
        return;
      }
      e_0.preventDefault();
      const {
        startY,
        startH
      } = dragRef.current;
      setHeight(clampHeight(startH + startY - e_0.clientY));
    };
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const onPointerMove = t3;
  let t4;
  if ($[4] !== onDismiss) {
    t4 = e_1 => {
      if (e_1.currentTarget.hasPointerCapture(e_1.pointerId)) {
        e_1.currentTarget.releasePointerCapture(e_1.pointerId);
      }
      dragRef.current = null;
      if (heightRef.current <= heightFromRatio(DISMISS_RATIO)) {
        onDismiss?.();
      }
    };
    $[4] = onDismiss;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const endDrag = t4;
  const t5 = `${height}px`;
  const t6 = `${height}px`;
  let t7;
  if ($[6] !== t5 || $[7] !== t6) {
    t7 = {
      "--drawer-size": t5,
      "--drawer-height": t6
    };
    $[6] = t5;
    $[7] = t6;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  const t8 = t7 as CSSProperties;
  const t9 = `${height}px`;
  let t10;
  if ($[9] !== t9) {
    t10 = {
      height: t9,
      maxHeight: `${MAX_RATIO * 100}dvh`,
      overflow: "hidden"
    };
    $[9] = t9;
    $[10] = t10;
  } else {
    t10 = $[10];
  }
  const t11 = t10 as CSSProperties;
  let t12;
  if ($[11] !== t11 || $[12] !== t8) {
    t12 = {
      root: t8,
      content: t11
    };
    $[11] = t11;
    $[12] = t8;
    $[13] = t12;
  } else {
    t12 = $[13];
  }
  const drawerStyles = t12;
  let t13;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <span className={styles.grip} aria-hidden={true} />;
    $[14] = t13;
  } else {
    t13 = $[14];
  }
  let t14;
  if ($[15] !== endDrag) {
    t14 = <button type="button" className={styles.handle} aria-label="Drag to resize drawer" onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={endDrag} onPointerCancel={endDrag}>{t13}</button>;
    $[15] = endDrag;
    $[16] = t14;
  } else {
    t14 = $[16];
  }
  const handle = t14;
  let t15;
  if ($[17] !== drawerStyles || $[18] !== handle) {
    t15 = {
      reset,
      drawerStyles,
      handle
    };
    $[17] = drawerStyles;
    $[18] = handle;
    $[19] = t15;
  } else {
    t15 = $[19];
  }
  return t15;
}